(() => {
  const formSelector = '[data-pet-predictive-search]';
  const searchCache = new Map();

  const escapeHtml = (value = '') =>
    String(value).replace(
      /[&<>"']/g,
      (character) =>
        ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#039;',
        })[character]
    );

  const resultLink = (url = '') => escapeHtml(url);

  const getResults = (payload) => payload?.resources?.results || {};

  class PetPredictiveSearch {
    constructor(form) {
      this.form = form;
      this.input = form.querySelector('.header-search-form__input');
      this.dropdown = form.querySelector('.header-predictive-search');
      this.predictiveUrl = form.dataset.predictiveSearchUrl;
      this.fullSearchUrl = form.dataset.fullSearchUrl;
      this.currency = form.dataset.currency || 'USD';
      this.locale = form.dataset.locale || document.documentElement.lang || 'en';
      this.abortController = null;
      this.timer = null;
      this.activeIndex = -1;
      this.links = [];

      if (!this.input || !this.dropdown || !this.predictiveUrl) return;

      this.form.dataset.petPredictiveSearchReady = 'true';
      this.input.addEventListener('input', this.handleInput);
      this.input.addEventListener('keydown', this.handleKeydown);
      this.input.addEventListener('focus', this.handleFocus);
      this.form.addEventListener('focusout', this.handleFocusOut);
      document.addEventListener('pointerdown', this.handleDocumentPointerDown);
    }

    handleInput = () => {
      window.clearTimeout(this.timer);
      const query = this.input.value.trim();

      if (query.length < 2) {
        this.close();
        return;
      }

      this.timer = window.setTimeout(() => this.search(query), 180);
    };

    handleFocus = () => {
      if (this.input.value.trim().length >= 2 && this.dropdown.innerHTML.trim()) {
        this.open();
      }
    };

    handleFocusOut = () => {
      window.setTimeout(() => {
        if (!this.form.contains(document.activeElement)) this.close();
      }, 120);
    };

    handleDocumentPointerDown = (event) => {
      if (!this.form.contains(event.target)) this.close();
    };

    handleKeydown = (event) => {
      if (this.dropdown.hidden || !this.links.length) {
        if (event.key === 'Escape') this.close();
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.setActive(this.activeIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.setActive(this.activeIndex - 1);
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        this.links[this.activeIndex].click();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    };

    setActive(index) {
      if (!this.links.length) return;

      this.activeIndex = (index + this.links.length) % this.links.length;
      this.links.forEach((link, linkIndex) => {
        link.setAttribute('aria-selected', linkIndex === this.activeIndex ? 'true' : 'false');
      });
      this.links[this.activeIndex].scrollIntoView({ block: 'nearest' });
    }

    formatPrice(value) {
      const amount = Number(value);
      if (!Number.isFinite(amount)) return String(value || '');

      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
        }).format(amount);
      } catch (_error) {
        return `${this.currency} ${amount.toFixed(2)}`;
      }
    }

    async search(query) {
      const cacheKey = query.toLocaleLowerCase();
      if (searchCache.has(cacheKey)) {
        this.render(query, searchCache.get(cacheKey));
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();
      this.dropdown.innerHTML = '<div class="header-predictive-search__loading">Finding pet essentials…</div>';
      this.open();

      const url = new URL(this.predictiveUrl, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'query,product,collection');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');

      try {
        const response = await fetch(url, {
          headers: { Accept: 'application/json' },
          signal: this.abortController.signal,
        });

        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

        const payload = await response.json();
        const results = getResults(payload);
        searchCache.set(cacheKey, results);

        if (query === this.input.value.trim()) this.render(query, results);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.render(query, {});
      }
    }

    render(query, results) {
      const queries = Array.isArray(results.queries) ? results.queries : [];
      const products = Array.isArray(results.products) ? results.products : [];
      const collections = Array.isArray(results.collections) ? results.collections : [];
      const sections = [];

      if (queries.length) {
        sections.push(`
          <section class="header-predictive-search__section">
            <h2 class="header-predictive-search__heading">Suggested searches</h2>
            <ul class="header-predictive-search__suggestions">
              ${queries
                .slice(0, 5)
                .map(
                  (suggestion) => `
                    <li>
                      <a class="header-predictive-search__suggestion" href="${resultLink(suggestion.url)}">
                        ${escapeHtml(suggestion.text)}
                      </a>
                    </li>
                  `
                )
                .join('')}
            </ul>
          </section>
        `);
      }

      if (products.length) {
        sections.push(`
          <section class="header-predictive-search__section">
            <h2 class="header-predictive-search__heading">Matching products</h2>
            <ul class="header-predictive-search__products">
              ${products
                .slice(0, 6)
                .map(
                  (product) => `
                    <li>
                      <a class="header-predictive-search__product" href="${resultLink(product.url)}">
                        <span class="header-predictive-search__product-image">
                          ${
                            product.image
                              ? `<img src="${resultLink(product.image)}" alt="" width="116" height="116" loading="lazy">`
                              : ''
                          }
                        </span>
                        <span class="header-predictive-search__product-copy">
                          <span class="header-predictive-search__product-title">${escapeHtml(product.title)}</span>
                          ${
                            product.price !== undefined && product.price !== null
                              ? `<span class="header-predictive-search__product-price">${escapeHtml(
                                  this.formatPrice(product.price)
                                )}</span>`
                              : ''
                          }
                        </span>
                      </a>
                    </li>
                  `
                )
                .join('')}
            </ul>
          </section>
        `);
      }

      if (collections.length) {
        sections.push(`
          <section class="header-predictive-search__section">
            <h2 class="header-predictive-search__heading">Related collections</h2>
            <ul class="header-predictive-search__collections">
              ${collections
                .slice(0, 5)
                .map(
                  (collection) => `
                    <li>
                      <a class="header-predictive-search__collection" href="${resultLink(collection.url)}">
                        ${escapeHtml(collection.title)}
                      </a>
                    </li>
                  `
                )
                .join('')}
            </ul>
          </section>
        `);
      }

      if (!sections.length) {
        sections.push(
          '<div class="header-predictive-search__empty">No close matches yet. Try a broader pet-care term.</div>'
        );
      }

      const allResultsUrl = new URL(this.fullSearchUrl, window.location.origin);
      allResultsUrl.searchParams.set('q', query);
      allResultsUrl.searchParams.set('options[prefix]', 'last');

      this.dropdown.innerHTML = `
        ${sections.join('')}
        <a class="header-predictive-search__view-all" href="${resultLink(allResultsUrl.toString())}">
          See all results for “${escapeHtml(query)}”
        </a>
      `;
      this.links = Array.from(this.dropdown.querySelectorAll('a'));
      this.activeIndex = -1;
      this.open();
    }

    open() {
      this.dropdown.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.dropdown.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.links.forEach((link) => link.removeAttribute('aria-selected'));
      this.activeIndex = -1;
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(formSelector).forEach((form) => {
      if (!form.dataset.petPredictiveSearchReady) new PetPredictiveSearch(form);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
